import {
  Button,
  Card,
  Flex,
  Icon,
  Link,
  PageHeader,
  Separator,
  Text,
  Textarea,
  Tooltip,
  useToast,
} from '@driftt/dds'
import { faArrowUpRightFromSquare } from '@driftt/dds.icons/light/faArrowUpRightFromSquare'
import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { RootStateOrAny, useSelector } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { getScenario, Scenario, updateScenario } from 'api/scenarios'
import { archiveScenario, restoreScenario } from 'api/scenarios'
import PlaybookPageLayout from 'components/PlaybookPage'
import { APP_PAGE_TITLES } from 'constants/pageTitles'

import ConfirmationModal from '../GuardrailsTable/ConfirmationModal'
import { AIPhrasesSamples } from './AIPhrasesSamples'
import { GuardrailsBreadcrumbs } from './GuardrailsBreadcrumbs'
import { GuardrailsEditorLoader } from './GuardrailsEditorLoader'
import { ValidationDrawer } from './ValidationDrawer'
import ModerationDrawer from './ValidationDrawer/ModerationDrawer'

type GuardrailsEditorProps = {
  match: {
    params: {
      id: string
    }
  }
} & RouteComponentProps

const GuardrailsEditor = ({ match, history }: GuardrailsEditorProps) => {
  const queryClient = useQueryClient()
  const {
    params: { id: scenarioId },
  } = match
  const toast = useToast()
  const orgId: string = useSelector(({ user }: RootStateOrAny) => user.orgId)

  const [editedScenario, setEditedScenario] = useState<
    Partial<Scenario> | undefined
  >()
  const [isEditReplyGuidelinesOpen, setIsEditReplyGuidelinesOpen] =
    useState(false)

  const {
    data: scenario,
    isLoading: isLoadingScenario,
    refetch,
  } = useQuery({
    queryKey: ['scenario', scenarioId],
    queryFn: async () => {
      const response = await getScenario({ scenarioId, orgId })
      return response.data
    },
    enabled: !!(orgId && scenarioId),
  })

  const isArchived = !!scenario?.archived
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [action, setAction] = useState<string>('')

  const onSaveScenario = useMutation(updateScenario, {
    onSuccess: () => {
      queryClient.invalidateQueries(['guardrails-list'])
      toast({
        variant: 'success',
        message: 'Guardrail successfully updated.',
      })
    },
    onError: () => {
      toast({
        variant: 'error',
        message: `Could not save guardrail`,
      })
    },
  })

  const onEditInstruction = (prompt?: string, replyGuidelines?: string) => {
    setEditedScenario({
      ...editedScenario,
      instruction: replyGuidelines || prompt,
    })
    toast({
      variant: 'success',
      message: 'Reply guidelines successfully updated.',
    })
  }

  const onSave = async () => {
    const payload = {
      scenarioId,
      ...scenario,
      ...editedScenario,
    }
    await onSaveScenario.mutateAsync({ orgId, payload })

    history.push('/ai-guardrails')
  }

  const onArchive = useMutation(archiveScenario, {
    onSuccess: () => {
      refetch()
      toast({
        variant: 'success',
        message: 'Guardrail successfully archived.',
      })
      setOpenModal(false)
    },
    onError: () => {
      toast({
        variant: 'error',
        message: `Could not archive guardrail`,
      })
      setOpenModal(false)
    },
  })

  const onRestore = useMutation(restoreScenario, {
    onSuccess: () => {
      refetch()
      toast({
        variant: 'success',
        message: `Guardrail successfully restored.`,
      })
      setOpenModal(false)
    },
    onError: () => {
      toast({
        variant: 'error',
        message: `Could not restore guardrail`,
      })
      setOpenModal(false)
    },
  })

  if (isLoadingScenario || !scenario) {
    return <GuardrailsEditorLoader />
  }

  const MODERATION_DETAILS =
    'The purpose of this guardrail is maintained by Google Vertex'
  const GENERAL_DETAILS = 'Determine the purpose of this guardrail.'
  const GOOGLE_VERTEX_URL =
    'https://cloud.google.com/natural-language/docs/moderating-text'
  const notCustomizableScenarios = [
    'self identity',
    'identity',
    'no human redirect',
    'meetings',
  ]

  const isCustomizable =
    !scenario || !notCustomizableScenarios.includes(scenario.name.toLowerCase())

  const isModerationScenario = scenario.name.toLowerCase() === 'moderation'

  return (
    <>
      <PlaybookPageLayout pageTitle={APP_PAGE_TITLES.GUARDRAIL}>
        <PageHeader.Root>
          <PageHeader.Main>
            <PageHeader.Title>{scenario.name}</PageHeader.Title>
            <PageHeader.Secondary>
              <GuardrailsBreadcrumbs guardrailName={scenario.name} />
            </PageHeader.Secondary>
            <PageHeader.RightElements>
              <Button
                loading={onSaveScenario.isLoading}
                onClick={
                  onSaveScenario.isLoading
                    ? () => {}
                    : () => {
                        setAction(isArchived ? 'restore' : 'archive')
                        setOpenModal(true)
                      }
                }
                disabled={!isCustomizable}
                variant="secondary"
              >
                {isArchived ? 'Restore' : 'Archive'}
              </Button>
              <Tooltip
                content={!isCustomizable && `There are no changes to be saved.`}
                side="left"
              >
                <Button
                  loading={onSaveScenario.isLoading}
                  onClick={onSaveScenario.isLoading ? () => {} : onSave}
                  disabled={!isCustomizable}
                >
                  Save
                </Button>
              </Tooltip>
            </PageHeader.RightElements>
          </PageHeader.Main>
          <Separator />
          <Flex
            css={{
              padding: '$3',
              backgroundColor: '$gray2',
              overflow: 'auto',
              paddingBottom: 150,
              zIndex: 0,
              '> div': {
                width: '1040px',
                maxWidth: '100%',
              },
            }}
            direction="column"
            alignItems="center"
            fullWidth
            fullHeight
            gap={2}
          >
            <Card>
              <Card.Header>
                <Card.Title>Guardrail details</Card.Title>
                <Flex alignItems="center" gap={1}>
                  <Text
                    css={{
                      color: '$gray10',
                    }}
                  >
                    {!isModerationScenario
                      ? GENERAL_DETAILS
                      : MODERATION_DETAILS}
                  </Text>
                  {isModerationScenario && (
                    <Link
                      variant="inline"
                      external={true}
                      href={GOOGLE_VERTEX_URL}
                    >
                      <Icon icon={faArrowUpRightFromSquare} /> Learn more
                    </Link>
                  )}
                </Flex>
              </Card.Header>
              <Card.Content>
                <Textarea
                  label="Description"
                  value={editedScenario?.prompt ?? scenario.prompt}
                  disabled
                  size="full-width"
                />
              </Card.Content>
            </Card>

            {scenario.scenarioExamples.length ? (
              <Card>
                <Card.Header>
                  <Card.Title>Trigger configuration</Card.Title>
                  <Text
                    css={{
                      color: '$gray10',
                    }}
                  >
                    Determine how a guardrail is triggered.
                  </Text>
                </Card.Header>

                <Card.Content>
                  <AIPhrasesSamples phrases={scenario.scenarioExamples} />
                </Card.Content>
              </Card>
            ) : null}
            <Card>
              <Card.Header>
                <Card.Title>Response configuration</Card.Title>
                <Text
                  css={{
                    color: '$gray10',
                  }}
                >
                  Determine how your chatbot responds when this guardrail is
                  triggered.
                </Text>
              </Card.Header>
              <Tooltip
                content={
                  !isCustomizable &&
                  'The reply guidelines are maintained by Drift and cannot be changed.'
                }
                side="bottom"
              >
                <Card.Content as={Flex} gap={1} direction="column">
                  <Textarea
                    label="Reply guidelines"
                    value={
                      editedScenario?.instruction ??
                      scenario.instruction?.trim() ??
                      ''
                    }
                    disabled
                    size="full-width"
                  />
                  {isCustomizable && (
                    <Button
                      variant="secondary"
                      onClick={() => setIsEditReplyGuidelinesOpen(true)}
                    >
                      {!isModerationScenario
                        ? 'Edit reply guidelines'
                        : 'Edit response'}
                    </Button>
                  )}
                </Card.Content>
              </Tooltip>
            </Card>
          </Flex>
        </PageHeader.Root>
      </PlaybookPageLayout>
      {!isModerationScenario ? (
        <ValidationDrawer
          type="guardrailReplyGuidelines"
          open={isEditReplyGuidelinesOpen}
          onClose={() => setIsEditReplyGuidelinesOpen(false)}
          savedValidaton={scenario.instruction?.trim() ?? ''}
          onAction={onEditInstruction}
        />
      ) : (
        <ModerationDrawer
          open={isEditReplyGuidelinesOpen}
          onClose={() => setIsEditReplyGuidelinesOpen(false)}
          savedResponse={scenario.instruction?.trim() ?? ''}
          onAction={onEditInstruction}
        />
      )}
      <ConfirmationModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        onAction={() => {
          action === 'archive'
            ? onArchive.mutate({ scenarioId: scenarioId, orgId })
            : onRestore.mutate({ scenarioId: scenarioId, orgId })
        }}
        loading={
          action === 'archive' ? onArchive.isLoading : onRestore.isLoading
        }
        action={action}
      />
    </>
  )
}

export default withRouter(GuardrailsEditor)
